import React from "react";

import type { NavItemConfig } from "@/types/nav";
import { paths } from "@/paths";

import { LinearIcon } from "../assets/LinearIcon";

// NOTE: We did not use React Components for Icons, because
//  you may want to get the config from the server.

// NOTE: First level of navItem elements are groups.

export interface DashboardConfig {
	// Overriden by Settings Context.
	layout: "horizontal" | "vertical";
	// Overriden by Settings Context.
	navColor: "blend_in" | "discrete" | "evident";
	navItems: NavItemConfig[];
	stats: {
		pieChart: {
			chartSize: number;
			chartTickness: number;
		};
	};
	api: {
		refetchInterval: number;
	};
}

export const dashboardConfig = {
	api: {
		refetchInterval: 15_000, // in milliseconds
		//staleInterval: 60_000, //  in milliseconds
	},
	stats: {
		pieChart: {
			chartSize: 75,
			chartTickness: 10,
		},
	},
	layout: "vertical",
	navColor: "evident",
	navItems: [
		{
            key: "dashboards",
            type: "common",
			title: "navbar:dashboards",
			visible: true,
			items: [
				{
                    key: "overview",
                    type: "common",
					title: "navbar:overview",
					href: paths.dashboard.overview,
                    iconLinear: <LinearIcon name="0003-home3" />,
					visible: true,
					feature: "overview:show",
				},
				{
                    key: "devices",
                    type: "common",
					title: "navbar:devices",
					href: paths.dashboard.device.list,
					//href: paths.dashboard.device.listOnline,
					icon: "devices",
					visible: true,
					matcher: {
						href: paths.dashboard.device.list,
						type: "equals",
					},
					feature: "device:list:show",
				},
				{
                    key: "currentDeviceSub",
                    type: "device",
					title: "DeviceIdPlaceholder",
                    feature: "device:overview:show",
                    deviceFeature: "overview",
					icon: "house",
					visible: false,
					disabled: true,
					forceOpen: true,

					//href: paths.dashboard.device.details("{deviceId}"),

					items: [
						{
                            key: "devices:device",
                            type: "device",
                            feature: "device:overview:show",
                            deviceFeature: "overview",
							title: "navbar:device:overview",
							href: paths.dashboard.device.details("{deviceId}"),
                            iconLinear: <LinearIcon name="0003-home3" />,
							visible: true,
                        },
                        {
                            key: "devices:control",
                            type: "device",
                            // Todo: richtigtes feature machen
                            feature: "control:show",
                            deviceFeature: "control",
                            title: "navbar:device:control",
                            href: paths.dashboard.device.control("{deviceId}"),
                            iconLinear: <LinearIcon name="0361-abacus" />,
                            visible: true,
                        },
                        {
                            key: "devices:primaryData",
                            type: "device",
                            title: "navbar:device:primaryData",
                            iconLinear: <LinearIcon name="0888-layers" />,
                            visible: true,
                            forceOpen: false,
                            disableOnClose: true,
                            feature: "device:primaryData:show",
                            deviceFeature: "primarydata",
                            items: [
                                {
                                    key: "devices:primaryData:general",
                                    type: "device",
                                    title: "pages:devices:primaryData:general",
                                    iconLinear: <LinearIcon name="0217-document" />,
                                    href: paths.dashboard.device.primaryData.general("{deviceId}"),
                                    visible: true,
                                    feature: "device:primaryData:general:show",
                                    deviceFeature: "primarydata",
                                },
                                {
                                    key: "devices:primaryData:contacts",
                                    type: "device",
                                    title: "pages:devices:primaryData:contact",
                                    iconLinear: <LinearIcon name="0283-contacts" />,
                                    href: paths.dashboard.device.primaryData.contact("{deviceId}"),
                                    visible: true,
                                    feature: "device:primaryData:contact:show",
                                    deviceFeature: "primarydata",
                                },
                            ],
                        },
						{
                            key: "devices:battery",
                            type: "device",
                            feature: "device:battery:show",
                            deviceFeature: "battery",
							title: "navbar:device:battery",
							href: paths.dashboard.device.battery("{deviceId}"),
                            iconLinear: <LinearIcon name="0460-battery-full" />,
							visible: true,
						},
						{
                            key: "devices:inverter",
                            type: "device",
                            feature: "device:inverter:show",
                            deviceFeature: "inverter",
							title: "navbar:device:inverter",
							href: paths.dashboard.device.inverter("{deviceId}"),
							icon: "inverter",
							visible: true,
                        },                        
						{
                            key: "devices:wallbox",
                            type: "device",
                            feature: "device:wallbox:show",
                            deviceFeature: "wallbox",
							title: "navbar:device:wallbox",
							href: paths.dashboard.device.wallbox("{deviceId}"),
							icon: "wallbox",
							visible: true,
						},
						{
                            key: "devices:events",
                            type: "device",
                            feature: "event:read",
                            deviceFeature: "event",
							title: "navbar:device:events",
							href: paths.dashboard.device.events("{deviceId}"),
                            iconLinear: <LinearIcon name="0855-notification-circle" />,
							badgeValue: 0,
							badgeColor: "error",
							visible: true,
							badgeVisible: false,
						},
						{
                            key: "devices:connections",
                            type: "device",
							// Todo: richtigtes feature machen
                            feature: "externconnections:show",
                            deviceFeature: "externconnections",
							title: "navbar:device:connections",
							href: paths.dashboard.device.connections("{deviceId}"),
							iconLinear: <LinearIcon name="0801-share2" />,
                            visible: true,

						},
						{
                            key: "devices:auxData",
                            type: "device",
							// Todo: richtigtes feature machen
                            feature: "aux:show",
                            deviceFeature: "auxiliaries",
							title: "navbar:device:auxData",
                            href: paths.dashboard.device.auxiliary("{deviceId}"),
							iconLinear: <LinearIcon name="0497-thermometer" />,
                            visible: true,
                            badgeVariant: "dot",
                            badgeVisible: false,
                            badgeColor: "default",
						},
						{
                            key: "devices:singleLineDiagram",
                            type: "device",
							// Todo: richtigtes feature machen
                            feature: "control:show",
                            deviceFeature: "singlelinediagram",
                            title: "navbar:device:sld",
                            href: paths.dashboard.device.singleLineDiagram("{deviceId}"),
                            iconLinear: <LinearIcon name="0644-site-map" />,
							visible: true,
						},
                      
						{
                            key: "devices:cases",
                            type: "device",
                            title: "navbar:cases",                            
							href: paths.dashboard.device.cases("{deviceId}"),
                            iconLinear: <LinearIcon name="0019-pencil4" />,
							visible: true,
                            feature: "case:show",
                            deviceFeature: "case",
						},
						{
                            key: "devices:operations",
                            type: "device",
							title: "navbar:operations",
							href: paths.dashboard.device.operations("{deviceId}"),
							iconLinear: <LinearIcon name="0204-clipboard-text" />,
							visible: true,
                            feature: "fieldreport:show",
                            deviceFeature: "fieldreport",
						},

						//{ key: "devices:settings", title: "navbar:device:settings", href: paths.dashboard.device.settings("{deviceId}"), icon: "house" },
						//{ key: "devices:information", title: "navbar:device:information", href: paths.dashboard.device.information("{deviceId}"), icon: "house" },
					],
				},
				{
                    key: "cases",
                    type: "common",
					title: "navbar:cases",
					href: paths.dashboard.cases.list,
                    iconLinear: <LinearIcon name="0019-pencil4" />,
					visible: true,
                    feature: "case:show",
                    deviceFeature: "case",
				},

				{
                    key: "reports",
                    type: "common",
					title: "navbar:reports:main",
					iconLinear: <LinearIcon name="0213-papers" />,
					visible: true,
					forceOpen: false,
					disableOnClose: true,
					feature: "fieldreport:show",
					items: [
						{
                            key: "operations",
                            type: "common",
							title: "navbar:operations",
							href: paths.dashboard.reports.operations.list,
							iconLinear: <LinearIcon name="0204-clipboard-text" />,
							visible: true,
                            feature: "fieldreport:show",
                            deviceFeature: "fieldreport",
						},
						{
                            key: "reports:monthly-reports",
                            type: "common",
							title: "navbar:reports:monthlyReports",
							iconLinear: <LinearIcon name="0396-calendar-text" />,
							href: paths.dashboard.reports.monthlyReport,
							visible: true,
                            feature: "monthreport:show",
                            deviceFeature: "monthreport",
						},
					],
                },
                {
                    key: "favorites",
                    type: "common",
                    title: "navbar:favorites",
                    href: paths.dashboard.favorites.list,
                    iconLinear: <LinearIcon name="0279-bookmark2" />,
                    visible: true,
                    feature: "case:show",
                    deviceFeature: "case",
                },

			],
		},
	],
} satisfies DashboardConfig;
